// =============================================================================
// Origin UI — src/services/orgChartPdfGenerator.ts
// OQT Organisation Chart PDF export (Oct 2026 — requested by Asal).
//
// A4 landscape, client-side jsPDF (same pattern as reportPdfGenerator.ts).
// Three scopes:
//   - all         : Leadership page (CEO + direct reports with team sizes),
//                   then one section per leader showing their full team.
//   - department  : Everyone in one department, rooted at the most senior
//                   person(s) whose manager sits outside the department.
//   - team        : One person and everyone below them.
//
// Section layout: root card at the top, a connector bus, then columns —
// one column per direct report who manages people (their whole sub-tree is
// listed vertically and indented), plus stacked columns for direct reports
// who manage nobody. Columns that are too tall continue in the next column
// slot ("cont."), and slots overflow onto further pages automatically.
// =============================================================================

import jsPDF from 'jspdf';
import { ORIGIN_LOGO_WHITE_PNG } from './originLogoWhite';

export interface OrgChartPerson {
  employeeId: string;
  name: string;
  jobTitle: string;
  departmentName: string;
  officeName: string;
  managerId: string | null;
}

export type OrgChartScope =
  | { kind: 'all' }
  | { kind: 'department'; department: string }
  | { kind: 'team'; rootId: string };

// ─── Brand ───
type RGB = [number, number, number];
const NAVY: RGB = [10, 34, 64];
const ORANGE: RGB = [255, 131, 0];
const TURQ: RGB = [0, 176, 185];
const WHITE: RGB = [255, 255, 255];
const G200: RGB = [226, 231, 236];
const G300: RGB = [205, 212, 219];
const G500: RGB = [107, 120, 133];
const G600: RGB = [74, 85, 104];
const NAVY_TEXT: RGB = [10, 30, 60];
const PALE: RGB = [248, 250, 251];

// ─── Page geometry (mm, A4 landscape) ───
const PAGE_W = 297;
const PAGE_H = 210;
const MARGIN = 12;
const USABLE_W = PAGE_W - MARGIN * 2;
const HEADER_H = 22;
const CONTENT_TOP = 30;
const CONTENT_BOTTOM = PAGE_H - 16;

const ROOT_W = 78;
const ROOT_H = 18;
const COL_GAP = 4;
const MAX_COLS = 6;
const MAX_COL_W = 58;
const HEAD_H = 14.5;
const MEMBER_H = 11.5;
const ENTRY_GAP = 2.2;
const INDENT = 3.5;
const MAX_INDENT_LEVELS = 4;
const CONT_LABEL_H = 5;

// ─── Internal structures ───
type Variant = 'root' | 'head' | 'member';

interface Entry {
  person: OrgChartPerson;
  depth: number;      // 0 = direct report of section root
  variant: Variant;
}

interface Segment {
  entries: Entry[];
  continuedFrom: string | null; // name of column head when this is a continuation
  connect: boolean;             // draw connector from the bus
}

interface Section {
  title: string;
  root: OrgChartPerson | null;
  rootExtra?: string;
  segments: Segment[];
  emptyNote?: string;
  intro?: string;      // short note shown above the columns (rootless sections)
  showDept?: boolean;  // show department on cards (mixed-department sections)
}

// =============================================================================
// Public entry point
// =============================================================================

export function generateOrgChartPdf(people: OrgChartPerson[], scope: OrgChartScope): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const byId = new Map(people.map(p => [p.employeeId, p]));

  // Children lookup, restricted to people in this export
  const allChildren = new Map<string, OrgChartPerson[]>();
  for (const p of people) {
    if (p.managerId && byId.has(p.managerId)) {
      const arr = allChildren.get(p.managerId) || [];
      arr.push(p);
      allChildren.set(p.managerId, arr);
    }
  }
  for (const arr of allChildren.values()) arr.sort((a, b) => a.name.localeCompare(b.name));

  const countCache = new Map<string, number>();
  const inProgress = new Set<string>(); // guards against reporting-line loops in the data
  const teamSize = (id: string, kids: (id: string) => OrgChartPerson[]): number => {
    if (countCache.has(id)) return countCache.get(id)!;
    if (inProgress.has(id)) return 0;
    inProgress.add(id);
    let n = 0;
    for (const c of kids(id)) n += 1 + teamSize(c.employeeId, kids);
    inProgress.delete(id);
    countCache.set(id, n);
    return n;
  };

  const today = new Date();
  const dateLabel = today.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  let scopeLabel = 'Entire organisation';
  let headcount = people.length;
  let fileScope = 'All';
  let firstPage = true;

  const newPage = (title: string) => {
    if (!firstPage) doc.addPage('a4', 'landscape');
    firstPage = false;
    drawHeader(doc, title, `${scopeLabel} · ${headcount} ${headcount === 1 ? 'person' : 'people'} · As at ${dateLabel}`);
  };

  if (scope.kind === 'all') {
    const kids = (id: string) => allChildren.get(id) || [];
    const tops = people.filter(p => !p.managerId || !byId.has(p.managerId));
    // The CEO is the top with the largest organisation beneath them
    const ceo = [...tops].sort((a, b) => teamSize(b.employeeId, kids) - teamSize(a.employeeId, kids))[0];

    if (ceo) {
      const leaders = [...kids(ceo.employeeId)].sort((a, b) =>
        a.departmentName.localeCompare(b.departmentName) || a.name.localeCompare(b.name));
      newPage('Leadership Team');
      drawLeadershipPage(doc, ceo, leaders, id => teamSize(id, kids), newPage);

      for (const leader of leaders) {
        if (kids(leader.employeeId).length === 0) continue;
        renderSection(doc, buildSection(
          `${leader.departmentName} — ${leader.name}`,
          leader,
          kids,
          `${teamSize(leader.employeeId, kids)} in team`), newPage);
      }
    }

    // Anyone not connected to the CEO's tree (missing / leaver manager)
    const unassigned = tops.filter(t => t !== ceo);
    if (unassigned.length > 0) {
      const section = buildRootlessSection('Reporting line not set in Origin', unassigned, kids);
      section.intro = 'These people have no line manager recorded in Origin, or their manager has left. Update the Line Manager on their profile to place them in the chart.';
      section.showDept = true;
      renderSection(doc, section, newPage);
    }
  } else if (scope.kind === 'department') {
    scopeLabel = `${scope.department} department`;
    fileScope = scope.department.replace(/[^\p{L}\p{N}]+/gu, '_');
    const members = people.filter(p => p.departmentName === scope.department);
    headcount = members.length;
    const memberIds = new Set(members.map(m => m.employeeId));
    const kids = (id: string) => (allChildren.get(id) || []).filter(c => memberIds.has(c.employeeId));
    const roots = members
      .filter(m => !m.managerId || !memberIds.has(m.managerId))
      .sort((a, b) => teamSize(b.employeeId, kids) - teamSize(a.employeeId, kids) || a.name.localeCompare(b.name));

    const withTeams = roots.filter(r => kids(r.employeeId).length > 0);
    const solo = roots.filter(r => kids(r.employeeId).length === 0);

    for (const r of withTeams) {
      renderSection(doc, buildSection(`${scope.department} — ${r.name}`, r, kids,
        `${teamSize(r.employeeId, kids)} in team`), newPage);
    }
    if (solo.length > 0) {
      const title = withTeams.length > 0 ? `${scope.department} — other members` : scope.department;
      renderSection(doc, buildRootlessSection(title, solo, kids), newPage);
    }
    if (members.length === 0) {
      newPage(scope.department);
      drawNote(doc, 'No active employees in this department.', CONTENT_TOP + 10);
    }
  } else {
    const kids = (id: string) => allChildren.get(id) || [];
    const root = byId.get(scope.rootId);
    if (root) {
      headcount = 1 + teamSize(root.employeeId, kids);
      scopeLabel = `${root.name}'s team`;
      fileScope = root.name.replace(/[^\p{L}\p{N}]+/gu, '_');
      const section = buildSection(`Team — ${root.name}`, root, kids,
        kids(root.employeeId).length > 0 ? `${teamSize(root.employeeId, kids)} in team` : undefined);
      if (kids(root.employeeId).length === 0) section.emptyNote = 'No direct reports.';
      renderSection(doc, section, newPage);
    }
  }

  if (firstPage) {
    newPage('Organisation Chart');
    drawNote(doc, 'No employees to show.', CONTENT_TOP + 10);
  }

  drawFooters(doc);
  const stamp = today.toISOString().slice(0, 10);
  doc.save(`OQT_Org_Chart_${fileScope}_${stamp}.pdf`);
}

// =============================================================================
// Section building
// =============================================================================

function buildSection(
  title: string,
  root: OrgChartPerson,
  kids: (id: string) => OrgChartPerson[],
  rootExtra?: string,
): Section {
  return { title, root, rootExtra, segments: buildSegments(kids(root.employeeId), kids) };
}

function buildRootlessSection(
  title: string,
  people: OrgChartPerson[],
  kids: (id: string) => OrgChartPerson[],
): Section {
  const sorted = [...people].sort((a, b) => a.name.localeCompare(b.name));
  return { title, root: null, segments: buildSegments(sorted, kids) };
}

/** Turn a set of direct reports into column segments that fit the page height. */
function buildSegments(directs: OrgChartPerson[], kids: (id: string) => OrgChartPerson[]): Segment[] {
  const available = CONTENT_BOTTOM - columnTop(true);
  const managers = directs.filter(d => kids(d.employeeId).length > 0);
  const individuals = directs.filter(d => kids(d.employeeId).length === 0);

  const columns: { head: string; entries: Entry[] }[] = [];

  for (const m of managers) {
    const entries: Entry[] = [{ person: m, depth: 0, variant: 'head' }];
    const seen = new Set<string>([m.employeeId]); // guards against reporting-line loops
    const walk = (id: string, depth: number) => {
      for (const c of kids(id)) {
        if (seen.has(c.employeeId)) continue;
        seen.add(c.employeeId);
        entries.push({ person: c, depth, variant: 'member' });
        walk(c.employeeId, depth + 1);
      }
    };
    walk(m.employeeId, 1);
    columns.push({ head: m.name, entries });
  }

  if (individuals.length > 0) {
    columns.push({
      head: 'Direct reports',
      entries: individuals.map(p => ({ person: p, depth: 0, variant: 'head' as Variant })),
    });
  }

  const segments: Segment[] = [];
  for (const col of columns) {
    let current: Segment = { entries: [], continuedFrom: null, connect: true };
    let used = 0;
    for (const e of col.entries) {
      const h = entryHeight(e) + ENTRY_GAP;
      if (current.entries.length > 0 && used + h > available) {
        segments.push(current);
        current = { entries: [], continuedFrom: col.head, connect: false };
        used = CONT_LABEL_H;
      }
      current.entries.push(e);
      used += h;
    }
    if (current.entries.length > 0) segments.push(current);
  }

  // Stacked individuals: every stack segment is a direct report group, so connect them all
  for (const s of segments) {
    if (s.entries.length > 0 && s.entries.every(e => e.variant === 'head') && s.continuedFrom === 'Direct reports') {
      s.connect = true;
    }
  }
  return segments;
}

function entryHeight(e: Entry): number {
  return e.variant === 'head' ? HEAD_H : MEMBER_H;
}

function columnTop(hasRoot: boolean): number {
  return hasRoot ? CONTENT_TOP + ROOT_H + 12 : CONTENT_TOP + 9;
}

// =============================================================================
// Rendering
// =============================================================================

function renderSection(doc: jsPDF, section: Section, newPage: (title: string) => void) {
  const hasRoot = section.root !== null;
  const colTop = columnTop(hasRoot);
  const segs = section.segments;
  const pageCount = Math.max(1, Math.ceil(segs.length / MAX_COLS));

  for (let p = 0; p < pageCount; p++) {
    newPage(pageCount > 1 ? `${section.title}  (${p + 1}/${pageCount})` : section.title);
    const pageSegs = segs.slice(p * MAX_COLS, (p + 1) * MAX_COLS);

    // Root card
    let rootCx = PAGE_W / 2;
    if (section.root) {
      const rx = (PAGE_W - ROOT_W) / 2;
      drawCard(doc, rx, CONTENT_TOP, ROOT_W, ROOT_H, section.root, 'root',
        p > 0 ? 'continued' : section.rootExtra);
      rootCx = rx + ROOT_W / 2;
    }

    if (section.intro && !section.root) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      doc.setTextColor(...G500);
      doc.text(section.intro, PAGE_W / 2, CONTENT_TOP + 3, { align: 'center', maxWidth: USABLE_W });
    }

    if (section.emptyNote && pageSegs.length === 0) {
      drawNote(doc, section.emptyNote, colTop + 4);
      continue;
    }
    if (pageSegs.length === 0) continue;

    const k = pageSegs.length;
    const colW = Math.min(MAX_COL_W, (USABLE_W - COL_GAP * (k - 1)) / k);
    const totalW = k * colW + (k - 1) * COL_GAP;
    const startX = (PAGE_W - totalW) / 2;

    // Connector bus
    const busY = colTop - 5;
    const connectXs = pageSegs
      .map((s, i) => ({ s, cx: startX + i * (colW + COL_GAP) + colW / 2 }))
      .filter(o => o.s.connect)
      .map(o => o.cx);

    if (section.root && connectXs.length > 0) {
      doc.setDrawColor(...G300);
      doc.setLineWidth(0.4);
      doc.line(rootCx, CONTENT_TOP + ROOT_H, rootCx, busY);
      const minX = Math.min(rootCx, ...connectXs);
      const maxX = Math.max(rootCx, ...connectXs);
      doc.line(minX, busY, maxX, busY);
      for (const cx of connectXs) doc.line(cx, busY, cx, colTop);
    }

    pageSegs.forEach((seg, i) => {
      const x = startX + i * (colW + COL_GAP);
      drawSegment(doc, seg, x, colTop, colW, !!section.showDept);
    });
  }
}

function drawSegment(doc: jsPDF, seg: Segment, x: number, top: number, colW: number, showDept: boolean) {
  let y = top;
  if (seg.continuedFrom) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(6.5);
    doc.setTextColor(...G500);
    doc.text(fit(doc, `${seg.continuedFrom} (cont.)`, colW), x, y + 3);
    y += CONT_LABEL_H;
  }

  // anchorY[d] = y from which a vertical guide for children at depth d+1 starts
  const anchorY: number[] = [];
  for (let d = 0; d <= MAX_INDENT_LEVELS + 1; d++) anchorY[d] = y;

  for (const e of seg.entries) {
    const h = entryHeight(e);
    const level = Math.min(e.depth, MAX_INDENT_LEVELS);
    const cardX = x + level * INDENT;
    const cardW = colW - level * INDENT;

    if (e.depth >= 1) {
      const guideX = x + (level - 1) * INDENT + 1.6;
      const midY = y + h / 2;
      doc.setDrawColor(...G300);
      doc.setLineWidth(0.3);
      doc.line(guideX, anchorY[level - 1], guideX, midY);
      doc.line(guideX, midY, cardX, midY);
      anchorY[level - 1] = midY;
    }

    drawCard(doc, cardX, y, cardW, h, e.person, e.variant, undefined, showDept);
    anchorY[level] = y + h;
    for (let d = level + 1; d <= MAX_INDENT_LEVELS + 1; d++) anchorY[d] = y + h;
    y += h + ENTRY_GAP;
  }
}

function drawLeadershipPage(
  doc: jsPDF,
  ceo: OrgChartPerson,
  leaders: OrgChartPerson[],
  size: (id: string) => number,
  newPage: (title: string) => void,
) {
  const rx = (PAGE_W - ROOT_W) / 2;
  const rootCx = rx + ROOT_W / 2;
  drawCard(doc, rx, CONTENT_TOP, ROOT_W, ROOT_H, ceo, 'root', `${size(ceo.employeeId)} in organisation`);
  if (leaders.length === 0) return;

  const perRow = MAX_COLS;
  const multiRow = leaders.length > perRow;
  const spineX = MARGIN + 1.5;
  const startX = multiRow ? MARGIN + 5 : MARGIN;
  const avail = PAGE_W - MARGIN - startX;
  const cardW = (avail - COL_GAP * (perRow - 1)) / perRow;
  const cardH = 19;
  const rowGap = 10;
  let y = CONTENT_TOP + ROOT_H + 12;
  let prevBusY: number | null = null;

  doc.setDrawColor(...G300);
  doc.setLineWidth(0.4);
  doc.line(rootCx, CONTENT_TOP + ROOT_H, rootCx, y - 5);

  for (let i = 0; i < leaders.length; i += perRow) {
    if (y + cardH > CONTENT_BOTTOM) {
      newPage('Leadership Team (cont.)');
      y = CONTENT_TOP + 8;
      prevBusY = null;
    }
    const row = leaders.slice(i, i + perRow);
    // Centre a short single row; multi-row layouts stay left-aligned on the spine
    const rowW = row.length * cardW + (row.length - 1) * COL_GAP;
    const rowX = multiRow ? startX : (PAGE_W - rowW) / 2;
    const busY = y - 5;
    const centres = row.map((_, j) => rowX + j * (cardW + COL_GAP) + cardW / 2);

    doc.setDrawColor(...G300);
    doc.setLineWidth(0.4);
    const busStart = multiRow ? spineX : Math.min(centres[0], rootCx);
    const busEnd = Math.max(centres[centres.length - 1], i === 0 ? rootCx : 0);
    doc.line(busStart, busY, busEnd, busY);
    if (multiRow && prevBusY !== null) doc.line(spineX, prevBusY, spineX, busY);
    prevBusY = busY;

    row.forEach((l, j) => {
      const x = rowX + j * (cardW + COL_GAP);
      doc.setDrawColor(...G300);
      doc.setLineWidth(0.4);
      doc.line(centres[j], busY, centres[j], y);
      const n = size(l.employeeId);
      drawCard(doc, x, y, cardW, cardH, l, 'head', n > 0 ? `${n} in team` : undefined, true);
    });
    y += cardH + rowGap;
  }
}

// ─── Card ───

function drawCard(
  doc: jsPDF,
  x: number, y: number, w: number, h: number,
  p: OrgChartPerson,
  variant: Variant,
  extra?: string,
  showDept = false,
) {
  const r = 1.6;
  if (variant === 'root') {
    doc.setFillColor(...NAVY);
    doc.roundedRect(x, y, w, h, r, r, 'F');
    doc.setFillColor(...ORANGE);
    doc.rect(x + r, y + h - 1.1, w - 2 * r, 1.1, 'F');

    const av = 5.2;
    const acx = x + 4 + av;
    const acy = y + h / 2 - 0.4;
    doc.setFillColor(...ORANGE);
    doc.circle(acx, acy, av, 'F');
    doc.setTextColor(...WHITE);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(initials(p.name), acx, acy + 1.1, { align: 'center' });

    const tx = acx + av + 3;
    const tw = x + w - tx - 3;
    doc.setFontSize(10.5);
    doc.text(fit(doc, p.name, tw), tx, y + 6.2);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(220, 228, 236);
    doc.text(fit(doc, p.jobTitle || '', tw), tx, y + 10.4);
    doc.setFontSize(6.8);
    doc.setTextColor(160, 176, 194);
    const meta = [p.departmentName, p.officeName].filter(Boolean).join(' · ') + (extra ? `  |  ${extra}` : '');
    doc.text(fit(doc, meta, tw), tx, y + 14.2);
    return;
  }

  // head / member
  doc.setFillColor(...WHITE);
  doc.setDrawColor(...G200);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, w, h, r, r, 'FD');

  if (variant === 'head') {
    doc.setFillColor(...ORANGE);
    doc.rect(x + r, y, w - 2 * r, 1, 'F');
  } else {
    doc.setFillColor(...TURQ);
    doc.rect(x, y + r, 1, h - 2 * r, 'F');
  }

  let tx = x + 2.6;
  if (variant === 'head') {
    const av = 3.4;
    const acx = x + 2.4 + av;
    const acy = y + h / 2 + 0.3;
    doc.setFillColor(...NAVY);
    doc.circle(acx, acy, av, 'F');
    doc.setTextColor(...WHITE);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.3);
    doc.text(initials(p.name), acx, acy + 0.8, { align: 'center' });
    tx = acx + av + 2;
  }
  const tw = x + w - tx - 1.8;
  const isHead = variant === 'head';

  doc.setTextColor(...NAVY_TEXT);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(isHead ? 7.6 : 7.1);
  doc.text(fit(doc, p.name, tw), tx, y + (isHead ? 5 : 4.1));

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...G600);
  doc.setFontSize(isHead ? 6.4 : 6.1);
  doc.text(fit(doc, p.jobTitle || '', tw), tx, y + (isHead ? 8.6 : 7.3));

  doc.setTextColor(...G500);
  doc.setFontSize(isHead ? 5.9 : 5.6);
  const metaParts = showDept ? [p.departmentName, p.officeName] : [p.officeName];
  const meta = metaParts.filter(Boolean).join(' · ');
  if (extra) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...TURQ);
    const ew = doc.getTextWidth(extra);
    doc.text(extra, x + w - 1.8, y + (isHead ? h - 2.2 : 10.2), { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...G500);
    doc.text(fit(doc, meta, Math.max(4, tw - ew - 2)), tx, y + (isHead ? h - 2.2 : 10.2));
  } else {
    doc.text(fit(doc, meta, tw), tx, y + (isHead ? h - 2.2 : 10.2));
  }
}

// ─── Header / footer / notes ───

function drawHeader(doc: jsPDF, title: string, subtitle: string) {
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, PAGE_W, HEADER_H, 'F');
  doc.setFillColor(...ORANGE);
  doc.rect(0, HEADER_H, PAGE_W, 1.5, 'F');

  // Origin logo (white Flow Paths mark + wordmark), then a thin divider
  const logoH = 11;
  const logoW = logoH * (1086 / 384);
  doc.addImage(ORIGIN_LOGO_WHITE_PNG, 'PNG', MARGIN - 1, (HEADER_H - logoH) / 2, logoW, logoH, 'origin-logo', 'FAST');
  const textX = MARGIN - 1 + logoW + 6;
  doc.setDrawColor(70, 92, 120);
  doc.setLineWidth(0.3);
  doc.line(textX - 3, 5, textX - 3, HEADER_H - 5);

  doc.setTextColor(...WHITE);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.text('OQT Organisation Chart', textX, 8.3);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(fit(doc, title, PAGE_W - MARGIN - textX - 100), textX, 17.2);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(200, 210, 222);
  doc.text('OQ Trading', PAGE_W - MARGIN, 8.3, { align: 'right' });
  doc.text(fit(doc, subtitle, 95), PAGE_W - MARGIN, 17.2, { align: 'right' });
}

function drawFooters(doc: jsPDF) {
  const n = doc.getNumberOfPages();
  for (let i = 1; i <= n; i++) {
    doc.setPage(i);
    doc.setDrawColor(...G200);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, PAGE_H - 11, PAGE_W - MARGIN, PAGE_H - 11);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...G500);
    doc.text('Generated from Origin — OQ Trading internal use only', MARGIN, PAGE_H - 6.5);
    doc.text(`Page ${i} of ${n}`, PAGE_W - MARGIN, PAGE_H - 6.5, { align: 'right' });
  }
}

function drawNote(doc: jsPDF, text: string, y: number) {
  doc.setFillColor(...PALE);
  doc.setDrawColor(...G200);
  doc.roundedRect(PAGE_W / 2 - 50, y, 100, 12, 2, 2, 'FD');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...G500);
  doc.text(text, PAGE_W / 2, y + 7.5, { align: 'center' });
}

// ─── Text helpers ───

function fit(doc: jsPDF, text: string, maxW: number): string {
  if (!text) return '';
  if (doc.getTextWidth(text) <= maxW) return text;
  let t = text;
  while (t.length > 1 && doc.getTextWidth(t + '...') > maxW) t = t.slice(0, -1);
  return t.trimEnd() + '...';
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const first = parts[0][0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}
